/**
 * DealsContent — web port of `apps/WunderGuide/components/deals/DealsOverlay.tsx`.
 *
 * Same data flow as RN useDeals + DealsOverlay:
 *   1. POST /deals/v1/discover via @wg/api/deals
 *   2. Show server-provided categories + blurb
 *   3. Render FeaturedDeals carousel + DealRow list
 *   4. If empty in DEV → fall back to MOCK_DEALS (parity with RN useDeals)
 *
 * Uses identical types from `@wg/types` so a deal payload from the agent's
 * Socket.IO `webData` event slots directly in.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dealsApi, { formatDiscount } from '../../api/dealsApi';
import type {
  DealSummary,
  CategoryOption,
  WeatherContext,
  LocationPoint,
} from '@wg/types';
import DealCouponModal from './DealCouponModal';
import CardCarousel from '../../common/CardCarousel';
import './DealsContent.css';

// Default location: Waikiki (Honolulu) — used as a placeholder until web
// gets a real location source. Mirrors RN's fallback when location denied.
const DEFAULT_LOCATION: LocationPoint = { lat: 21.2793, lng: -157.8294 };

const FALLBACK_CATEGORIES: {
  key: string;
  label: string;
  icon: React.ReactNode;
}[] = [
  { key: 'all', label: 'All', icon: <GridIcon /> },
  { key: 'food', label: 'Food', icon: <ForkIcon /> },
  { key: 'shopping', label: 'Shopping', icon: <BagIcon /> },
  { key: 'entertainment', label: 'Entertainment', icon: <TicketIcon /> },
  { key: 'services', label: 'Services', icon: <WrenchIcon /> },
  { key: 'travel', label: 'Travel', icon: <PlaneIcon /> },
];

// Mirrors apps/WunderGuide/useDeals.ts MOCK_DEALS — for empty-state parity.
const MOCK_DEALS: DealSummary[] = [
  {
    id: 'mock-deal-1',
    partner_id: 'partner-1',
    owner_name: 'Island Brew Coffee',
    owner_logo:
      'https://images.unsplash.com/photo-1559496417-e7f25cb247f3?w=100&h=100&fit=crop',
    owner_type: 'merchant',
    title: '20% Off Any Coffee',
    description: 'Start your day with our locally roasted Hawaiian coffee.',
    deal_type: 'percentage',
    discount_value: 20,
    thumbnail_url:
      'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=300&fit=crop',
    status: 'active',
    ends_at: new Date(Date.now() + 2 * 86_400_000).toISOString(),
    distance_miles: 0.3,
    category: 'food',
    priority: 15,
  },
  {
    id: 'mock-deal-2',
    partner_id: 'partner-2',
    owner_name: 'Aloha Surf Shop',
    owner_logo:
      'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=100&h=100&fit=crop',
    owner_type: 'merchant',
    title: 'Free Board Wax with Rental',
    description: 'Get free premium board wax with any surfboard rental.',
    deal_type: 'freebie',
    thumbnail_url:
      'https://images.unsplash.com/photo-1502680390469-be75c86b636f?w=400&h=300&fit=crop',
    status: 'active',
    ends_at: new Date(Date.now() + 12 * 3_600_000).toISOString(),
    distance_miles: 0.8,
    category: 'shopping',
    priority: 12,
  },
  {
    id: 'mock-deal-3',
    partner_id: 'partner-3',
    owner_name: 'Poke Paradise',
    owner_logo:
      'https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=100&h=100&fit=crop',
    owner_type: 'merchant',
    title: 'BOGO 50% Off Poke Bowls',
    description: 'Buy one poke bowl, get the second at half price.',
    deal_type: 'bogo',
    discount_value: 50,
    thumbnail_url:
      'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop',
    status: 'active',
    ends_at: new Date(Date.now() + 7 * 86_400_000).toISOString(),
    distance_miles: 1.2,
    category: 'food',
    priority: 10,
  },
  {
    id: 'mock-deal-4',
    partner_id: 'partner-4',
    owner_name: 'Sunset Yoga Studio',
    owner_type: 'merchant',
    title: 'First Class Free',
    description: 'Your first yoga class is on us.',
    deal_type: 'freebie',
    thumbnail_url:
      'https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=400&h=300&fit=crop',
    status: 'active',
    ends_at: new Date(Date.now() + 14 * 86_400_000).toISOString(),
    distance_miles: 2.1,
    category: 'services',
    priority: 5,
  },
  {
    id: 'mock-deal-5',
    partner_id: 'partner-5',
    owner_name: 'Luau Paradise',
    owner_type: 'merchant',
    title: '$10 Off Luau Tickets',
    description: 'Experience authentic Hawaiian luau entertainment.',
    deal_type: 'fixed_amount',
    discount_value: 10,
    thumbnail_url:
      'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=400&h=300&fit=crop',
    status: 'active',
    ends_at: new Date(Date.now() + 5 * 86_400_000).toISOString(),
    distance_miles: 3.5,
    category: 'entertainment',
    priority: 5,
  },
  {
    id: 'mock-deal-6',
    partner_id: 'partner-6',
    owner_name: 'Island Tours Co',
    owner_type: 'merchant',
    title: '15% Off Island Tours',
    description: 'Explore the island with our guided tours.',
    deal_type: 'percentage',
    discount_value: 15,
    thumbnail_url:
      'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=300&fit=crop',
    status: 'active',
    ends_at: new Date(Date.now() + 10 * 86_400_000).toISOString(),
    distance_miles: 0.5,
    category: 'travel',
    priority: 11,
  },
];

const DealsContent: React.FC = () => {
  const [deals, setDeals] = useState<DealSummary[]>([]);
  const [featured, setFeatured] = useState<DealSummary[]>([]);
  const [serverCategories, setServerCategories] = useState<
    CategoryOption[] | null
  >(null);
  const [blurb, setBlurb] = useState<string | null>(null);
  const [, setWeather] = useState<WeatherContext | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeDeal, setActiveDeal] = useState<DealSummary | null>(null);

  const fetchDeals = useCallback(async (category: string) => {
    setError(null);
    try {
      let weatherCtx: WeatherContext | null = null;
      try {
        const w: any = await dealsApi.getWeather(DEFAULT_LOCATION);
        if (w) {
          weatherCtx = {
            condition: w.condition ?? w.icon ?? 'sunny',
            temp_f: w.temp_f,
            humidity: w.humidity,
            is_raining: w.is_raining ?? false,
          };
          setWeather(weatherCtx);
        }
      } catch {
        /* weather optional */
      }

      const response = await dealsApi.discoverDeals({
        location: DEFAULT_LOCATION,
        radius_miles: 5,
        weather: weatherCtx ?? undefined,
        local_time: new Date().toISOString(),
        category: category !== 'all' ? category : undefined,
        limit: 25,
      });

      let nextDeals = response.deals ?? [];
      let nextFeatured = response.featured ?? [];
      const nextCats = response.categories ?? null;
      const nextBlurb = response.blurb ?? null;

      if (
        nextDeals.length === 0 &&
        nextFeatured.length === 0 &&
        (nextCats?.length ?? 0) === 0
      ) {
        nextFeatured = MOCK_DEALS.filter(d => (d.priority ?? 0) >= 10);
        nextDeals = MOCK_DEALS.filter(d => (d.priority ?? 0) < 10);
      }

      setDeals(nextDeals);
      setFeatured(nextFeatured);
      setServerCategories(nextCats);
      setBlurb(nextBlurb);
    } catch (err: any) {
      // 404 / 401 / network: fall back to mock data so UI never goes blank.
      setDeals(MOCK_DEALS.filter(d => (d.priority ?? 0) < 10));
      setFeatured(MOCK_DEALS.filter(d => (d.priority ?? 0) >= 10));
      if (err?.response?.status && err.response.status !== 404) {
        setError('Could not load deals.');
      }
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeals(selectedCategory);
  }, [fetchDeals, selectedCategory]);

  const categories = useMemo(() => {
    if (serverCategories && serverCategories.length > 0) {
      return serverCategories.map(c => ({
        key: c.key,
        label: c.label,
        count: c.count,
        icon: iconForCategory(c.key),
      }));
    }
    return FALLBACK_CATEGORIES.map(c => ({ ...c, count: 0 }));
  }, [serverCategories]);

  return (
    <div className="wg-deals">
      {/* Category chips (horizontal scroll) */}
      <div className="wg-deals__filters">
        {categories.map(cat => (
          <button
            key={cat.key}
            type="button"
            className={`wg-deals__chip${selectedCategory === cat.key ? ' is-active' : ''}`}
            onClick={() => setSelectedCategory(cat.key)}
          >
            <span className="wg-deals__chip-icon">{cat.icon}</span>
            <span>{cat.label}</span>
            {cat.count > 0 && cat.key !== 'all' && (
              <span className="wg-deals__chip-count">({cat.count})</span>
            )}
          </button>
        ))}
      </div>

      {/* Blurb banner */}
      {blurb && (
        <div className="wg-deals__blurb">
          <span>{blurb}</span>
        </div>
      )}

      {/* Featured carousel */}
      {featured.length > 0 && (
        <section className="wg-deals__section">
          <div className="wg-deals__section-title">Featured</div>
          <FeaturedRow deals={featured} onPress={setActiveDeal} />
        </section>
      )}

      {/* Deals list */}
      <section className="wg-deals__section">
        <div className="wg-deals__section-header">
          <span className="wg-deals__section-title">Deals</span>
          <span className="wg-deals__section-count">({deals.length})</span>
        </div>

        {initialLoading && (
          <div className="wg-deals__center">
            <Spinner />
          </div>
        )}

        {!initialLoading && error && (
          <div className="wg-deals__empty">
            <AlertIcon />
            <div className="wg-deals__empty-text">{error}</div>
            <button
              type="button"
              className="wg-deals__retry"
              onClick={() => fetchDeals(selectedCategory)}
            >
              Retry
            </button>
          </div>
        )}

        {!initialLoading && !error && deals.length === 0 && (
          <div className="wg-deals__empty">
            <TagIcon />
            <div className="wg-deals__empty-text">No deals nearby</div>
            <div className="wg-deals__empty-sub">
              Try a different category or check back later
            </div>
          </div>
        )}

        {!initialLoading && deals.length > 0 && (
          <div className="wg-deals__list">
            {deals.map(d => (
              <DealRow key={d.id} deal={d} onPress={setActiveDeal} />
            ))}
          </div>
        )}
      </section>

      <DealCouponModal deal={activeDeal} onClose={() => setActiveDeal(null)} />
    </div>
  );
};

export default DealsContent;

/* ── Sub-components ─────────────────────────────────────────────────── */

const FeaturedRow: React.FC<{
  deals: DealSummary[];
  onPress: (d: DealSummary) => void;
}> = ({ deals, onPress }) => {
  return (
    <CardCarousel
      data={deals}
      keyFor={d => d.id}
      autoPlayInterval={7500}
      transitionDuration={800}
      height={232}
      className="wg-deals__featured-stage"
      renderItem={(d, _i, isActive) => (
        <article
          className="wg-deals__featured"
          role="button"
          tabIndex={isActive ? 0 : -1}
          onClick={() => isActive && onPress(d)}
          onKeyDown={e => {
            if (isActive && (e.key === 'Enter' || e.key === ' ')) onPress(d);
          }}
        >
          <div
            className="wg-deals__featured-img"
            style={
              d.thumbnail_url
                ? { backgroundImage: `url(${d.thumbnail_url})` }
                : undefined
            }
          />
          <div className="wg-deals__featured-body">
            <div className="wg-deals__featured-merchant">{d.owner_name}</div>
            <div className="wg-deals__featured-title">{d.title}</div>
            <div className="wg-deals__featured-meta">
              {formatDiscount(d.deal_type, d.discount_value) && (
                <span className="wg-deals__featured-badge">
                  {formatDiscount(d.deal_type, d.discount_value)}
                </span>
              )}
              {typeof d.distance_miles === 'number' && (
                <span className="wg-deals__featured-distance">
                  {formatDistance(d.distance_miles)}
                </span>
              )}
            </div>
          </div>
        </article>
      )}
    />
  );
};

const DealRow: React.FC<{
  deal: DealSummary;
  onPress: (d: DealSummary) => void;
}> = ({ deal, onPress }) => {
  const discount = formatDiscount(deal.deal_type, deal.discount_value);
  const distance =
    typeof deal.distance_miles === 'number'
      ? formatDistance(deal.distance_miles)
      : null;

  return (
    <button
      type="button"
      className="wg-deals__row"
      onClick={() => onPress(deal)}
    >
      <span className="wg-deals__row-logo">
        {deal.owner_logo ? <img src={deal.owner_logo} alt="" /> : <StoreIcon />}
      </span>
      <span className="wg-deals__row-text">
        <span className="wg-deals__row-title">{deal.title}</span>
        <span className="wg-deals__row-meta">
          <span className="wg-deals__row-merchant">
            {deal.owner_name || 'Merchant'}
          </span>
          {distance && (
            <>
              <span className="wg-deals__row-dot">·</span>
              <span>{distance}</span>
            </>
          )}
        </span>
      </span>
      {discount && <span className="wg-deals__row-badge">{discount}</span>}
      <span className="wg-deals__row-chev">
        <ChevronRightIcon />
      </span>
    </button>
  );
};

function formatDistance(miles: number): string {
  if (miles < 0.1) return 'Nearby';
  if (miles < 1) return `${Math.round(miles * 5280)} ft`;
  return `${miles.toFixed(1)} mi`;
}

function iconForCategory(key: string): React.ReactNode {
  switch (key) {
    case 'food':
      return <ForkIcon />;
    case 'shopping':
      return <BagIcon />;
    case 'entertainment':
      return <TicketIcon />;
    case 'services':
      return <WrenchIcon />;
    case 'travel':
      return <PlaneIcon />;
    default:
      return <GridIcon />;
  }
}

/* ── Icons ─────────────────────────────────────────────────────────── */

function GridIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}
function ForkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M7 2v8a3 3 0 0 0 6 0V2" />
      <path d="M10 10v12" />
      <path d="M17 2v6c0 1.5 1 2.5 2 2.5V22" />
    </svg>
  );
}
function BagIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 2 4 6v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6l-2-4Z" />
      <path d="M4 6h16" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}
function TicketIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 9V7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z" />
      <path d="M13 5v14" />
    </svg>
  );
}
function WrenchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M14.7 6.3a4 4 0 0 0 5 5L21 12a8 8 0 1 1-9-9z" />
    </svg>
  );
}
function PlaneIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 22 4 22 3l-1 1c-1 1-3 2-4.5 2.5L8 8 5 5l-3 3 7 5-3 7 3-1 5-3 4 1z" />
    </svg>
  );
}
function StoreIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m2 9 1-5h18l1 5" />
      <path d="M3 9v11a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V9" />
      <path d="M3 9c.6 1.2 1.8 2 3 2 1.5 0 2.5-1 3-2" />
      <path d="M9 9c.5 1 1.5 2 3 2s2.5-1 3-2" />
      <path d="M15 9c.5 1 1.5 2 3 2s2.4-.8 3-2" />
    </svg>
  );
}
function ChevronRightIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
function TagIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 12 12 3H3v9l9 9z" />
      <circle cx="7" cy="7" r="1.4" />
    </svg>
  );
}
function AlertIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="8" x2="12" y2="13" />
      <circle cx="12" cy="16.5" r="0.6" fill="currentColor" />
    </svg>
  );
}
function Spinner() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="wg-deals__spinner"
      aria-hidden
    >
      <path d="M12 2a10 10 0 1 0 10 10" />
    </svg>
  );
}
